import {useEffect, useState} from "react";
import ApiIcon from "@mui/icons-material/CodeOutlined";
import {Dialog} from "../base/Dialog";
import {useResourceManagement} from "../../hooks/resourceHooks";
import {swallowException} from "../../utils/appUtils";
import {getResourceErrorMessage, isResourceLoading, isResourceSucceeded} from "../../utils/resourceUtils";
import {getUserCredentials, rotateUserCredentials} from "../../services/userCredentialsService";
import {ApiCredentialsCard} from "../users/ApiCredentialsCard";
import {OAuthCredentials} from "../../types/domain/oauthCredentials";
import {Frame} from "../base/Frame";
import {useTranslation} from "react-i18next";
import {Button} from "@mui/material";

export interface Props {
    open: boolean;
    onClose: (isSuccessful: boolean) => void;
    userId: string;
}

export const UserApiCredentialsDialog: React.FC<Props> = ({open, onClose, userId}) => {
    const {t} = useTranslation();
    const credentialsManagement = useResourceManagement({getUserCredentials, rotateUserCredentials});
    const {getUserCredentials: getCredentials} = credentialsManagement;
    const [credentials, setCredentials] = useState<OAuthCredentials | undefined>(undefined);

    useEffect(() => {
        getCredentials(userId).catch(swallowException);
    }, [userId, getCredentials]);

    useEffect(() => {
        if (isResourceSucceeded(credentialsManagement.resource)) {
            setCredentials(credentialsManagement.resource.data);
        }
    }, [credentialsManagement, setCredentials]);

    const onRotateCredentials = () => {
        credentialsManagement.reset();
        credentialsManagement.rotateUserCredentials(userId).catch(swallowException);
    };

    return (
        <Dialog
            width="50rem"
            title={t("dialogs.user_api_credentials.title")}
            titleIcon={<ApiIcon color="primary" />}
            headerContent={
                <Button
                    href="/api-v1-swagger"
                    target="_blank"
                    sx={{whiteSpace: "nowrap", paddingY: "0.5rem", paddingX: "1.2rem", marginY: "-0.5rem"}}
                >
                    {t("api_credentials.api_swagger_link")}
                </Button>
            }
            cancelTitle={t("dialogs.actions.close")}
            open={open}
            error={getResourceErrorMessage(credentialsManagement.resource)}
            onClose={() => onClose(false)}
        >
            <Frame>
                <ApiCredentialsCard
                    isLoading={isResourceLoading(credentialsManagement.resource)}
                    credentials={credentials}
                    onRotateCredentials={onRotateCredentials}
                />
            </Frame>
        </Dialog>
    );
};
